import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useClientes } from "../hooks/useClientes";
import { useEntries } from "../hooks/useEntries";
import { formatAmount } from "../config/currencies";
import { Contact, Smartphone, Search, X, Plus, Pencil, Trash2 } from "lucide-react";

export default function Contactos() {
  const { lang, currency } = useAuth();
  const navigate = useNavigate();
  const { clientes, loading, error: loadError, addCliente, updateCliente, deleteCliente } = useClientes();
  const { entries } = useEntries();
  const fmt = n => formatAmount(n, currency);

  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const emptyForm = { nombre:"", telefono:"", dni:"", direccion:"", email:"", extra1:"", extra2:"", extra3:"" };
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  // La API del selector de contactos del móvil (Contact Picker) solo
  // existe en Chrome para Android — se comprueba antes de mostrar el
  // botón, para que no aparezca (ni falle) en el resto de navegadores.
  const contactPickerSupported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;

  // Abre el selector nativo del móvil y aplica lo elegido (nombre y
  // teléfono) al formulario que se le indique — se usa tal cual al
  // EDITAR (ahí no tiene sentido comprobar duplicados, ya es una ficha
  // existente).
  async function importarDesdeMovil(applyFn) {
    if (!contactPickerSupported) return;
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (picked && picked.length > 0) {
        const p = picked[0];
        applyFn({
          nombre: (p.name && p.name[0]) || "",
          telefono: (p.tel && p.tel[0]) || "",
        });
      }
    } catch (err) {
      // El usuario cerró el selector sin elegir nada, o el navegador lo
      // rechazó — no es un fallo real que haya que mostrar como error.
      console.warn("Selector de contactos cancelado:", err);
    }
  }

  // Compara solo los dígitos del teléfono (ignora espacios, guiones,
  // prefijos con "+", etc.) — es más fiable que comparar por nombre,
  // porque dos personas pueden llamarse igual pero no comparten número.
  const soloDigitos = s => (s || "").replace(/\D/g, "");
  function buscarDuplicadoPorTelefono(telefono) {
    const norm = soloDigitos(telefono);
    if (!norm) return null;
    return clientes.find(cl => soloDigitos(cl.telefono) === norm) || null;
  }

  // Igual que importarDesdeMovil, pero solo para el formulario de AÑADIR
  // uno nuevo: si el teléfono importado ya pertenece a un contacto que
  // existe, se pregunta si se quiere usar ese en vez de crear un
  // duplicado.
  async function importarParaNuevoContacto() {
    if (!contactPickerSupported) return;
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (!picked || picked.length === 0) return;
      const p = picked[0];
      const nombre = (p.name && p.name[0]) || "";
      const telefono = (p.tel && p.tel[0]) || "";

      const existente = buscarDuplicadoPorTelefono(telefono);
      if (existente) {
        const usarExistente = window.confirm(
          lang==="en"
            ? `You already have a contact with this phone: "${existente.nombre}". Use that one instead of creating a new contact?`
            : `Ya tienes un contacto guardado con este teléfono: "${existente.nombre}". ¿Quieres usar ese contacto en vez de crear uno nuevo?`
        );
        if (usarExistente) {
          setShowForm(false);
          setForm(emptyForm);
          setExpandedId(existente.id);
          return;
        }
        // Si dice que no, se sigue y se crea uno nuevo con estos datos
      }
      setForm(f => ({ ...f, nombre: nombre||f.nombre, telefono: telefono||f.telefono }));
    } catch (err) {
      console.warn("Selector de contactos cancelado:", err);
    }
  }
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = clientes.filter(c => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.nombre||"").toLowerCase().includes(q)
        || (c.dni||"").toLowerCase().includes(q)
        || (c.email||"").toLowerCase().includes(q);
  });

  async function handleAdd(e) {
    e.preventDefault();
    setError("");
    if (!form.nombre.trim()) {
      setError(lang==="en" ? "Enter a name" : "Introduce un nombre");
      return;
    }
    setSaving(true);
    try {
      await addCliente({
        nombre: form.nombre.trim(), telefono: form.telefono.trim(), dni: form.dni.trim(), direccion: form.direccion.trim(),
        email: form.email.trim(), extra1: form.extra1.trim(), extra2: form.extra2.trim(), extra3: form.extra3.trim(),
      });
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      setError(lang==="en" ? `Something went wrong: ${err.message||err}` : `Algo falló: ${err.message||err}`);
    } finally {
      setSaving(false);
    }
  }

  function startEdit(c) {
    setEditingId(c.id);
    setEditForm({
      nombre: c.nombre||"", telefono: c.telefono||"", dni: c.dni||"", direccion: c.direccion||"",
      email: c.email||"", extra1: c.extra1||"", extra2: c.extra2||"", extra3: c.extra3||"",
    });
  }

  async function saveEdit(id) {
    if (!editForm.nombre.trim()) return;
    setSaving(true);
    try {
      await updateCliente(id, {
        nombre: editForm.nombre.trim(), telefono: editForm.telefono.trim(), dni: editForm.dni.trim(), direccion: editForm.direccion.trim(),
        email: editForm.email.trim(), extra1: editForm.extra1.trim(), extra2: editForm.extra2.trim(), extra3: editForm.extra3.trim(),
      });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  // Historial de un contacto concreto: todas las entradas del Historial
  // (ventas o compras) vinculadas a él a través de "clienteId" — se
  // calcula al vuelo, no se guarda nada duplicado.
  function historialDe(clienteId) {
    return entries
      .filter(e => e.clienteId === clienteId)
      .sort((a,b) => {
        const parse = dt => { if(!dt) return 0; const [d,t]=dt.split(" "); const [dd,mm,yy]=(d||"").split("/"); return new Date(yy,mm-1,dd,...(t||"00:00").split(":")).getTime(); };
        return parse(b.datetime) - parse(a.datetime);
      });
  }

  if (loading) return null;

  if (loadError) {
    return (
      <div className="page">
        <div className="alert alert-error">
          {lang==="en"
            ? `Couldn't load contacts: ${loadError}`
            : `No se pudieron cargar los contactos: ${loadError}`}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:20}}>
        <h1 style={{fontSize:22,fontWeight:800,display:"flex",alignItems:"center",gap:8}}><Contact size={22}/> {lang==="en" ? "Contacts" : "Contactos"}</h1>
        <button className="btn btn-primary" onClick={()=>setShowForm(!showForm)} style={{display:"flex",alignItems:"center",gap:6}}>
          {showForm ? <X size={15}/> : <Plus size={15}/>} {showForm ? (lang==="en"?"Cancel":"Cancelar") : (lang==="en"?"Add contact":"Añadir contacto")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="card" style={{marginBottom:20,display:"flex",flexDirection:"column",gap:12}}>
          {error && <div className="alert alert-error">{error}</div>}

          {contactPickerSupported && (
            <button type="button" className="btn btn-outline" style={{justifyContent:"center"}}
                    onClick={importarParaNuevoContacto}>
              <Smartphone size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Import from my contacts" : "Importar desde mis contactos"}
            </button>
          )}

          <div className="form-group">
            <label className="form-label">{lang==="en"?"Name":"Nombre"} *</label>
            <input className="form-input" value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})}
                   placeholder={lang==="en"?"Contact or company name":"Nombre del contacto o empresa"} required/>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <div className="form-group" style={{flex:1,minWidth:160}}>
              <label className="form-label">{lang==="en"?"Phone":"Teléfono"}</label>
              <input className="form-input" type="tel" value={form.telefono} onChange={e=>setForm({...form,telefono:e.target.value})}/>
            </div>
            <div className="form-group" style={{flex:1,minWidth:160}}>
              <label className="form-label">DNI/NIF</label>
              <input className="form-input" value={form.dni} onChange={e=>setForm({...form,dni:e.target.value})}/>
            </div>
            <div className="form-group" style={{flex:1,minWidth:160}}>
              <label className="form-label">{lang==="en"?"Email":"Correo electrónico"}</label>
              <input className="form-input" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{lang==="en"?"Address":"Dirección"}</label>
            <input className="form-input" value={form.direccion} onChange={e=>setForm({...form,direccion:e.target.value})}/>
          </div>
          <div className="form-group">
            <label className="form-label">{lang==="en"?"Extra fields (optional)":"Campos adicionales (opcional)"}</label>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <input className="form-input" value={form.extra1} onChange={e=>setForm({...form,extra1:e.target.value})}
                     placeholder={lang==="en"?"e.g. reference":"ej. referencia"}/>
              <input className="form-input" value={form.extra2} onChange={e=>setForm({...form,extra2:e.target.value})}
                     placeholder={lang==="en"?"e.g. contact person":"ej. persona de contacto"}/>
              <input className="form-input" value={form.extra3} onChange={e=>setForm({...form,extra3:e.target.value})}
                     placeholder={lang==="en"?"e.g. notes":"ej. notas"}/>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving} style={{justifyContent:"center"}}>
            {saving ? "..." : (lang==="en"?"Save contact":"Guardar contacto")}
          </button>
        </form>
      )}

      <div className="form-group" style={{marginBottom:16}}>
        <input className="form-input" value={search} onChange={e=>setSearch(e.target.value)}
               placeholder={lang==="en" ? "Search contacts by name, DNI or email..." : "Buscar contactos por nombre, DNI o email..."}/>
      </div>

      {filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Contact size={26}/></div>
          <h3>{clientes.length===0 ? (lang==="en"?"No contacts yet":"Aún no hay contactos") : (lang==="en"?"No results":"Sin resultados")}</h3>
          <p>{clientes.length===0 ? (lang==="en"?"Add your first contact above":"Añade tu primer contacto arriba") : (lang==="en"?"Try a different search":"Prueba con otra búsqueda")}</p>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.map(c => {
            const isExpanded = expandedId === c.id;
            const isEditing = editingId === c.id;
            const historial = isExpanded ? historialDe(c.id) : [];
            // Importante: una "operación combinada" (ingreso + gasto juntos)
            // se guarda con el NETO como amount/type — el bruto de cada lado
            // vive en incomeAmount/expenseAmount. Sin esto, el gasto que va
            // "dentro" de una combinada nunca se sumaba al total de gastos.
            let totalIngresos = 0, totalGastos = 0;
            historial.forEach(e => {
              if (e.isCombined) {
                totalIngresos += e.incomeAmount || 0;
                totalGastos += e.expenseAmount || 0;
              } else if (e.type === "income") {
                totalIngresos += e.amount;
              } else if (e.type === "expense") {
                totalGastos += e.amount;
              }
            });
            return (
              <div key={c.id} className="card card-sm">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}
                     onClick={()=>{ setExpandedId(isExpanded?null:c.id); setEditingId(null); }}>
                  <div>
                    <div style={{fontWeight:700,fontSize:15}}>{c.nombre}</div>
                    <div style={{fontSize:12,color:"var(--gray-500)"}}>
                      {[c.telefono, c.dni, c.email].filter(Boolean).join(" · ") || (lang==="en"?"No extra details":"Sin más datos")}
                    </div>
                  </div>
                  <span style={{fontSize:12,color:"var(--gray-400)"}}>{isExpanded?"▲":"▼"}</span>
                </div>

                {isExpanded && (
                  <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--border)"}}
                       onClick={ev=>ev.stopPropagation()}>
                    {isEditing ? (
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        {contactPickerSupported && (
                          <button type="button" className="btn btn-outline btn-sm" style={{justifyContent:"center"}}
                                  onClick={()=>importarDesdeMovil(({nombre,telefono})=>setEditForm(f=>({...f, nombre: nombre||f.nombre, telefono: telefono||f.telefono})))}>
                            <Smartphone size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Import from my contacts" : "Importar desde mis contactos"}
                          </button>
                        )}
                        <input className="form-input" value={editForm.nombre} onChange={e=>setEditForm({...editForm,nombre:e.target.value})} placeholder={lang==="en"?"Name":"Nombre"}/>
                        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                          <input className="form-input" style={{flex:1,minWidth:140}} type="tel" value={editForm.telefono} onChange={e=>setEditForm({...editForm,telefono:e.target.value})} placeholder={lang==="en"?"Phone":"Teléfono"}/>
                          <input className="form-input" style={{flex:1,minWidth:140}} value={editForm.dni} onChange={e=>setEditForm({...editForm,dni:e.target.value})} placeholder="DNI/NIF"/>
                          <input className="form-input" style={{flex:1,minWidth:140}} value={editForm.email} onChange={e=>setEditForm({...editForm,email:e.target.value})} placeholder="Email"/>
                        </div>
                        <input className="form-input" value={editForm.direccion} onChange={e=>setEditForm({...editForm,direccion:e.target.value})} placeholder={lang==="en"?"Address":"Dirección"}/>
                        <input className="form-input" value={editForm.extra1} onChange={e=>setEditForm({...editForm,extra1:e.target.value})} placeholder={lang==="en"?"Extra field 1":"Campo adicional 1"}/>
                        <input className="form-input" value={editForm.extra2} onChange={e=>setEditForm({...editForm,extra2:e.target.value})} placeholder={lang==="en"?"Extra field 2":"Campo adicional 2"}/>
                        <input className="form-input" value={editForm.extra3} onChange={e=>setEditForm({...editForm,extra3:e.target.value})} placeholder={lang==="en"?"Extra field 3":"Campo adicional 3"}/>
                        <div style={{display:"flex",gap:8}}>
                          <button className="btn btn-primary btn-sm" style={{flex:1,justifyContent:"center"}} disabled={saving} onClick={()=>saveEdit(c.id)}>
                            {saving?"...":(lang==="en"?"Save":"Guardar")}
                          </button>
                          <button className="btn btn-outline btn-sm" style={{flex:1,justifyContent:"center"}} onClick={()=>setEditingId(null)}>
                            {lang==="en"?"Cancel":"Cancelar"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{display:"flex",flexDirection:"column",gap:6,fontSize:13,marginBottom:14}}>
                          {c.telefono && <div><strong>{lang==="en"?"Phone":"Teléfono"}:</strong> {c.telefono}</div>}
                          {c.dni && <div><strong>DNI/NIF:</strong> {c.dni}</div>}
                          {c.direccion && <div><strong>{lang==="en"?"Address":"Dirección"}:</strong> {c.direccion}</div>}
                          {c.email && <div><strong>Email:</strong> {c.email}</div>}
                          {c.extra1 && <div>{c.extra1}</div>}
                          {c.extra2 && <div>{c.extra2}</div>}
                          {c.extra3 && <div>{c.extra3}</div>}
                          {c.createdAt?.toDate && (
                            <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2}}>
                              {lang==="en" ? "Contact since " : "Contacto desde el "}
                              {c.createdAt.toDate().toLocaleDateString(lang==="en"?"en-GB":"es-ES")}
                            </div>
                          )}
                        </div>

                        {historial.length > 0 && (
                          <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                            {totalIngresos > 0 && (
                              <div style={{background:"var(--green-l,#dcfce7)",color:"var(--green,#16a34a)",borderRadius:20,padding:"6px 12px",fontSize:12,fontWeight:700}}>
                                +{fmt(totalIngresos)} {lang==="en"?"received":"cobrado"}
                              </div>
                            )}
                            {totalGastos > 0 && (
                              <div style={{background:"var(--red-l,#fee2e2)",color:"var(--red,#dc2626)",borderRadius:20,padding:"6px 12px",fontSize:12,fontWeight:700}}>
                                -{fmt(totalGastos)} {lang==="en"?"paid":"pagado"}
                              </div>
                            )}
                          </div>
                        )}

                        <p style={{fontSize:11,fontWeight:600,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:".04em",marginBottom:8}}>
                          {lang==="en" ? "Purchase/sale history" : "Historial de compras/ventas"} ({historial.length})
                        </p>
                        {historial.length === 0 ? (
                          <p style={{fontSize:13,color:"var(--gray-400)",marginBottom:14}}>
                            {lang==="en" ? "No entries linked to this contact yet." : "Este contacto todavía no tiene movimientos vinculados."}
                          </p>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
                            {historial.slice(0,10).map(e => (
                              <div key={e.id} onClick={()=>navigate(`/historial?entryId=${e.id}`)}
                                   style={{display:"flex",justifyContent:"space-between",padding:"7px 10px",background:"var(--gray-50)",borderRadius:8,fontSize:13,cursor:"pointer"}}
                                   onMouseEnter={ev=>ev.currentTarget.style.background="var(--gray-100)"}
                                   onMouseLeave={ev=>ev.currentTarget.style.background="var(--gray-50)"}>
                                <span>{e.desc || e.cat}<span style={{color:"var(--gray-400)"}}> · {e.datetime}</span></span>
                                {e.isCombined ? (
                                  <span style={{textAlign:"right"}}>
                                    {e.incomeAmount > 0 && <strong style={{color:"var(--green,#16a34a)"}}>+{fmt(e.incomeAmount)}</strong>}
                                    {e.incomeAmount > 0 && e.expenseAmount > 0 && " "}
                                    {e.expenseAmount > 0 && <strong style={{color:"var(--red,#dc2626)"}}>-{fmt(e.expenseAmount)}</strong>}
                                  </span>
                                ) : (
                                  <strong style={{color:e.type==="income"?"var(--green,#16a34a)":"var(--red,#dc2626)"}}>
                                    {e.type==="income"?"+":"-"}{fmt(e.amount)}
                                  </strong>
                                )}
                              </div>
                            ))}
                            {historial.length > 10 && (
                              <p style={{fontSize:12,color:"var(--gray-400)",textAlign:"center"}}>
                                {lang==="en" ? `+${historial.length-10} more` : `+${historial.length-10} más`}
                              </p>
                            )}
                          </div>
                        )}

                        <div style={{display:"flex",gap:8}}>
                          <button className="btn btn-outline btn-sm" onClick={()=>startEdit(c)} style={{display:"flex",alignItems:"center",gap:5}}><Pencil size={13}/> {lang==="en"?"Edit":"Editar"}</button>
                          <button className="btn btn-danger btn-sm"
                            onClick={()=>{ if(window.confirm(lang==="en"?"Delete this contact? Linked entries will keep their data but stop showing here.":"¿Eliminar este contacto? Los movimientos vinculados conservarán sus datos, pero dejarán de aparecer aquí.")) deleteCliente(c.id); }}>
                            <Trash2 size={13} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Delete":"Eliminar"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
